// services/lead-generation/ESGLeadGenerationService.js
// Updated with European registry integration

import { companiesHouseAPI } from './data-sources/CompaniesHouseAPI.js';
import { euRegistryOrchestrator, euVIESService } from './data-sources/EURegistryAPI.js';
import { esgComplianceChecker } from './compliance/ESGComplianceChecker.js';
import { ESGComplianceScorer } from '../../components/lead-generation/scoring/scorers/ESGComplianceScorer.js';

// Create ESG scorer instance
const esgScorer = new ESGComplianceScorer();

export class ESGLeadGenerationService {
  constructor() {
    this.dataSources = {
      companiesHouse: companiesHouseAPI,
      euRegistry: euRegistryOrchestrator,
      vies: euVIESService
    };
    
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
    
    // EU country configurations for targeted searches
    this.euCountries = {
      high_priority: ['germany', 'france', 'netherlands', 'ireland'],
      medium_priority: ['belgium', 'austria', 'denmark', 'sweden'],
      low_priority: ['poland', 'czech', 'hungary', 'slovakia']
    };
  }

  /**
   * Search for ESG compliance leads across EU registries
   * @param {Object} searchParams - Search parameters
   * @param {Object} filters - ESG-specific filters including EU countries
   * @returns {Promise<Array>} Array of qualified ESG leads from EU
   */
  async searchESGLeads(searchParams, filters = {}) {
    const { query, maxResults = 100 } = searchParams;
    
    try {
      console.log('Starting EU ESG lead search for:', query);
      console.log('Filters applied:', filters);
      
      // Step 1: Determine target countries based on filters
      const targetCountries = this.determineTargetCountries(filters);
      console.log('Target countries:', targetCountries);
      
      // Step 2: Get raw company data from multiple EU sources
      const rawCompanies = await this.aggregateEUCompanyData(query, maxResults, targetCountries);
      console.log('Raw EU companies found:', rawCompanies.length);
      
      if (rawCompanies.length === 0) {
        console.log('No EU companies found, returning empty array');
        return [];
      }
      
      // Step 3: Apply ESG compliance analysis with EU-specific rules
      console.log('Analyzing EU ESG compliance...');
      const esgAnalyzedCompanies = await this.analyzeEUESGCompliance(rawCompanies);
      console.log('ESG analyzed companies:', esgAnalyzedCompanies.length);
      
      // Step 4: Apply ESG-specific filters with EU considerations
      console.log('Applying EU ESG filters:', filters);
      const filteredCompanies = this.applyEUESGFilters(esgAnalyzedCompanies, filters);
      console.log('Filtered companies:', filteredCompanies.length);
      
      // Step 5: Calculate ESG lead scores with EU weighting
      console.log('Calculating EU ESG lead scores...');
      const scoredLeads = await this.scoreEUESGLeads(filteredCompanies);
      console.log('Scored leads:', scoredLeads.length);
      
      // Step 6: Rank and return top leads
      const rankedLeads = this.rankLeads(scoredLeads);
      console.log('Final ranked EU leads:', rankedLeads.length);
      
      return rankedLeads.slice(0, maxResults);
      
    } catch (error) {
      console.error('EU ESG lead generation failed:', error);
      throw new Error(`Failed to generate EU ESG leads: ${error.message}`);
    }
  }

  /**
   * Determine which EU countries to search based on filters and business strategy
   * @param {Object} filters - Filter criteria
   * @returns {Array} Array of country codes to search
   */
  determineTargetCountries(filters) {
    // If specific country filter is set
    if (filters.country && filters.country !== 'all') {
      const countryMapping = {
        'uk': ['uk'],
        'germany': ['germany', 'de'],
        'france': ['france', 'fr'],
        'netherlands': ['netherlands', 'nl'],
        'ireland': ['ireland', 'ie'],
        'other_eu': ['belgium', 'austria', 'denmark', 'sweden', 'poland']
      };
      
      return countryMapping[filters.country] || [filters.country];
    }
    
    // If compliance type suggests prioritization
    if (filters.complianceType === 'csrd_mandatory') {
      // Focus on large EU economies with many large companies
      return ['germany', 'france', 'netherlands', 'uk'];
    }
    
    if (filters.complianceType === 'vsme_eligible') {
      // Include more countries as SMEs are everywhere
      return [...this.euCountries.high_priority, ...this.euCountries.medium_priority];
    }
    
    // Default: search high-priority EU countries
    return this.euCountries.high_priority;
  }

  /**
   * Aggregate company data from multiple EU sources
   * @param {string} query - Search query
   * @param {number} maxResults - Maximum total results
   * @param {Array} targetCountries - Countries to search
   * @returns {Promise<Array>} Combined company data from EU
   */
  async aggregateEUCompanyData(query, maxResults, targetCountries) {
    const cacheKey = `eu_search_${query}_${targetCountries.join(',')}_${maxResults}`;
    
    // Check cache first
    if (this.cache.has(cacheKey)) {
      const cachedData = this.cache.get(cacheKey);
      if (Date.now() - cachedData.timestamp < this.cacheTimeout) {
        console.log('Using cached EU company data');
        return cachedData.data;
      }
    }

    const allCompanies = [];
    const maxPerRegistry = Math.ceil(maxResults / targetCountries.length);
    
    // Search UK Companies House if included
    if (targetCountries.includes('uk')) {
      try {
        const ukCompanies = await this.dataSources.companiesHouse.searchCompanies({
          query,
          items_per_page: Math.min(maxPerRegistry, 50)
        });
        
        // Standardize UK data format for EU compatibility
        const standardizedUKCompanies = ukCompanies.map(company => ({
          ...company,
          id: `uk_${company.company_number || company.id}`,
          country: 'United Kingdom',
          source: 'UK Companies House',
          registryNumber: company.company_number,
          employees: this.estimateUKEmployees(company),
          turnover: this.estimateUKTurnover(company),
          esgMetadata: {
            industryESGRisk: this.assessUKIndustryRisk(company.sic_codes),
            complianceDeadline: this.getUKCSRDDeadline(company)
          }
        }));
        
        allCompanies.push(...standardizedUKCompanies);
        console.log(`Found ${standardizedUKCompanies.length} UK companies`);
      } catch (error) {
        console.warn('UK Companies House search failed:', error.message);
      }
    }

    // Search EU registries
    const euCountries = targetCountries.filter(country => country !== 'uk');
    if (euCountries.length > 0) {
      try {
        const euSearchResult = await this.dataSources.euRegistry.searchAllRegistries(query, {
          countries: euCountries,
          maxResultsPerRegistry: maxPerRegistry,
          includeUK: false,
          activeOnly: true
        });
        
        allCompanies.push(...euSearchResult.companies);
        console.log(`Found ${euSearchResult.companies.length} EU companies`);
        
      } catch (error) {
        console.warn('EU registry search failed:', error.message);
      }
    }

    // Remove duplicates and cache results
    const uniqueCompanies = this.deduplicateCompanies(allCompanies);
    
    this.cache.set(cacheKey, {
      data: uniqueCompanies,
      timestamp: Date.now()
    });

    return uniqueCompanies;
  }

  /**
   * Analyze ESG compliance requirements for EU companies
   * @param {Array} companies - Raw company data from EU sources
   * @returns {Promise<Array>} Companies with EU ESG analysis
   */
  async analyzeEUESGCompliance(companies) {
    const analyzedCompanies = [];

    for (const company of companies) {
      try {
        // Run EU-specific ESG assessment
        const esgAssessment = await this.performEUESGAssessment(company);
        
        // Add ESG assessment to company data
        analyzedCompanies.push({
          ...company,
          esgAssessment
        });

      } catch (error) {
        console.warn(`EU ESG analysis failed for ${company.name}:`, error);
        // Include company without ESG data rather than excluding it
        analyzedCompanies.push({
          ...company,
          esgAssessment: {
            primaryRequirement: 'ANALYSIS_FAILED',
            overallPriority: 'LOW',
            error: error.message
          }
        });
      }
    }

    return analyzedCompanies;
  }

  /**
   * Perform EU-specific ESG compliance assessment
   * @param {Object} company - Company data
   * @returns {Promise<Object>} ESG assessment with EU-specific rules
   */
  async performEUESGAssessment(company) {
    const assessment = {
      primaryRequirement: 'NOT_APPLICABLE',
      overallPriority: 'LOW',
      csrd: {},
      vsme: {},
      industryRisk: {},
      recommendations: []
    };

    const employees = company.employees || 0;
    const turnover = company.turnover || 0;
    const country = company.country;
    const isListed = company.esgMetadata?.isListedCompany || false;
    const industryCode = company.industryCode || company.industry;

    // CSRD Assessment (EU-wide)
    if (this.isEUCountry(country)) {
      // Large companies (immediate CSRD requirement)
      if (employees >= 500 || turnover >= 40000000) {
        assessment.primaryRequirement = 'CSRD_MANDATORY';
        assessment.overallPriority = 'CRITICAL';
        assessment.csrd = {
          deadline: '2025',
          scope: 'Full CSRD reporting required',
          preparationTime: 'Immediate implementation needed',
          estimatedComplexity: 'HIGH'
        };
      }
      // Listed SMEs (2026 deadline)
      else if (isListed && employees >= 10) {
        assessment.primaryRequirement = 'CSRD_LISTED_SME';
        assessment.overallPriority = 'HIGH';
        assessment.csrd = {
          deadline: '2026',
          scope: 'Listed SME CSRD requirements',
          preparationTime: '12-18 months preparation',
          estimatedComplexity: 'MEDIUM'
        };
      }
      // VSME eligible companies
      else if (employees >= 10 && employees <= 250 && turnover >= 2000000 && turnover <= 50000000) {
        assessment.primaryRequirement = 'VSME_VOLUNTARY';
        assessment.overallPriority = 'MEDIUM';
        assessment.vsme = {
          framework: 'Voluntary Simplified Standards',
          benefits: 'Competitive advantage, stakeholder trust',
          implementationTime: '6-12 months',
          estimatedComplexity: 'LOW'
        };
      }
    }

    // Industry-specific risk assessment
    assessment.industryRisk = this.assessEUIndustryRisk(industryCode, country);

    // Generate EU-specific recommendations
    assessment.recommendations = this.generateEURecommendations(assessment, company);

    return assessment;
  }

  /**
   * Apply ESG filters with EU-specific considerations
   * @param {Array} companies - Companies with ESG analysis
   * @param {Object} filters - Filter criteria
   * @returns {Array} Filtered companies
   */
  applyEUESGFilters(companies, filters) {
    let filtered = [...companies];

    // Filter by compliance type (enhanced for EU)
    if (filters.complianceType && filters.complianceType !== 'all') {
      filtered = filtered.filter(company => {
        const requirement = company.esgAssessment?.primaryRequirement;
        
        switch (filters.complianceType) {
          case 'csrd_mandatory':
            return requirement === 'CSRD_MANDATORY';
          case 'csrd_listed_sme':
            return requirement === 'CSRD_LISTED_SME';
          case 'vsme_eligible':
            return requirement === 'VSME_VOLUNTARY';
          case 'approaching_threshold':
            return this.isApproachingEUThreshold(company);
          case 'future_preparation':
            return requirement === 'NOT_APPLICABLE';
          default:
            return true;
        }
      });
    }

    // Filter by EU industry risk level
    if (filters.industryRisk && filters.industryRisk !== 'all') {
      filtered = filtered.filter(company => {
        const riskLevel = company.esgAssessment?.industryRisk?.level ||
                        company.esgIndicators?.industryRisk;
        return riskLevel?.toLowerCase() === filters.industryRisk.toLowerCase();
      });
    }

    // Filter by EU country/region
    if (filters.country && filters.country !== 'all') {
      filtered = filtered.filter(company => {
        const country = company.country.toLowerCase();
        
        switch (filters.country) {
          case 'uk':
            return country.includes('united kingdom') || country.includes('uk');
          case 'germany':
            return country.includes('germany') || country.includes('deutschland');
          case 'france':
            return country.includes('france');
          case 'netherlands':
            return country.includes('netherlands');
          case 'ireland':
            return country.includes('ireland');
          case 'nordic':
            return ['denmark', 'sweden', 'finland', 'norway'].some(c => country.includes(c));
          case 'central_eu':
            return ['austria', 'belgium', 'luxembourg'].some(c => country.includes(c));
          case 'eastern_eu':
            return ['poland', 'czech', 'hungary', 'slovakia'].some(c => country.includes(c));
          case 'other_eu':
            return this.isEUCountry(company.country) && 
                   !['united kingdom', 'germany', 'france', 'netherlands', 'ireland'].some(c => country.includes(c));
          default:
            return true;
        }
      });
    }

    // Filter by company size with EU thresholds
    if (filters.companySize && filters.companySize !== 'all') {
      filtered = filtered.filter(company => {
        const employees = company.employees || 0;
        const turnover = company.turnover || 0;
        
        switch (filters.companySize) {
          case 'large':
            return employees >= 500 || turnover >= 40000000; // CSRD large company thresholds
          case 'medium':
            return employees >= 50 && employees < 500;
          case 'small':
            return employees >= 10 && employees < 50;
          case 'micro':
            return employees < 10;
          case 'vsme_range':
            return employees >= 10 && employees <= 250 && 
                   turnover >= 2000000 && turnover <= 50000000;
          default:
            return true;
        }
      });
    }

    // Filter by CSRD reporting deadline
    if (filters.reportingDeadline && filters.reportingDeadline !== 'all') {
      filtered = filtered.filter(company => {
        const deadline = company.esgAssessment?.csrd?.deadline ||
                        company.esgIndicators?.complianceDeadline;
        
        if (!deadline) return filters.reportingDeadline === 'none';
        
        return deadline.includes(filters.reportingDeadline);
      });
    }

    return filtered;
  }

  /**
   * Check if company is approaching EU ESG thresholds
   * @param {Object} company - Company data
   * @returns {boolean} True if approaching EU thresholds
   */
  isApproachingEUThreshold(company) {
    const { employees, turnover } = company;
    
    // Approaching VSME lower thresholds
    const approachingVSME = (
      employees >= 8 && employees <= 12
    ) || (
      turnover >= 1800000 && turnover <= 2200000 // €1.8M - €2.2M
    );

    // Approaching CSRD thresholds
    const approachingCSRD = (
      employees >= 450 && employees <= 550
    ) || (
      turnover >= 36000000 && turnover <= 44000000 // €36M - €44M
    );

    // Approaching listed SME thresholds (if listing status changes)
    const approachingListed = employees >= 8 && employees <= 15;

    return approachingVSME || approachingCSRD || approachingListed;
  }

  /**
   * Calculate ESG lead scores with EU-specific weighting
   * @param {Array} companies - Filtered EU companies
   * @returns {Promise<Array>} Companies with EU ESG scores
   */
  async scoreEUESGLeads(companies) {
    const scoredLeads = [];

    for (const company of companies) {
      try {
        // Use EU-enhanced scoring
        const scoringResult = await this.calculateEUESGScore(company);
        
        scoredLeads.push({
          ...company,
          totalScore: scoringResult.totalScore,
          scoreBreakdown: scoringResult.breakdown,
          priority: scoringResult.priority,
          reasoning: scoringResult.reasoning,
          euSpecificInsights: scoringResult.euInsights
        });

      } catch (error) {
        console.warn(`EU ESG scoring failed for ${company.name}:`, error);
        // Include with default score rather than excluding
        scoredLeads.push({
          ...company,
          totalScore: 30,
          priority: 'LOW',
          reasoning: ['EU scoring unavailable due to incomplete data']
        });
      }
    }

    return scoredLeads;
  }

  /**
   * Calculate EU-specific ESG lead score
   * @param {Object} company - Company with ESG assessment
   * @returns {Promise<Object>} Detailed scoring result
   */
  async calculateEUESGScore(company) {
    let totalScore = 0;
    const breakdown = {};
    const reasoning = [];
    const euInsights = [];

    // 1. EU Compliance Urgency (40% weight)
    const complianceScore = this.scoreEUComplianceUrgency(company);
    breakdown.complianceUrgency = complianceScore;
    totalScore += complianceScore * 0.4;

    if (complianceScore > 80) {
      reasoning.push('High compliance urgency due to imminent CSRD deadline');
      euInsights.push('Company faces mandatory CSRD reporting - immediate software need');
    }

    // 2. EU Industry Risk (30% weight)  
    const industryScore = this.scoreEUIndustryRisk(company);
    breakdown.industryRisk = industryScore;
    totalScore += industryScore * 0.3;

    if (industryScore > 70) {
      reasoning.push('High-ESG-risk industry requires comprehensive reporting');
      euInsights.push('Industry sector subject to intensive ESG scrutiny in EU');
    }

    // 3. EU Market Size & Opportunity (20% weight)
    const marketScore = this.scoreEUMarketOpportunity(company);
    breakdown.marketOpportunity = marketScore;
    totalScore += marketScore * 0.2;

    // 4. Geographic Strategy Priority (10% weight)
    const geoScore = this.scoreEUGeographicPriority(company);
    breakdown.geographicPriority = geoScore;
    totalScore += geoScore * 0.1;

    if (this.isHighPriorityEUMarket(company.country)) {
      reasoning.push(`${company.country} is a strategic EU market for ESG software`);
    }

    // Determine priority level
    let priority = 'LOW';
    if (totalScore >= 85) priority = 'CRITICAL';
    else if (totalScore >= 70) priority = 'HIGH';
    else if (totalScore >= 50) priority = 'MEDIUM';

    // Add EU-specific insights
    euInsights.push(...this.generateEUMarketInsights(company));

    return {
      totalScore: Math.round(totalScore),
      breakdown,
      priority,
      reasoning,
      euInsights,
      methodology: 'EU ESG Compliance Scoring v2.0'
    };
  }

  /**
   * Score EU compliance urgency
   */
  scoreEUComplianceUrgency(company) {
    const requirement = company.esgAssessment?.primaryRequirement;
    
    switch (requirement) {
      case 'CSRD_MANDATORY':
        return 95; // Immediate compliance required
      case 'CSRD_LISTED_SME':
        return 80; // 2026 deadline approaching
      case 'VSME_VOLUNTARY':
        return 60; // Voluntary but competitive advantage
      default:
        return company.esgIndicators?.csrdApplicable ? 75 : 30;
    }
  }

  /**
   * Score EU industry ESG risk
   */
  scoreEUIndustryRisk(company) {
    const riskLevel = company.esgAssessment?.industryRisk?.level ||
                     company.esgIndicators?.industryRisk ||
                     'LOW';
    
    const riskScores = {
      'HIGH': 85,
      'MEDIUM': 60, 
      'LOW': 35
    };
    
    return riskScores[riskLevel] || 35;
  }

  /**
   * Score EU market opportunity
   */
  scoreEUMarketOpportunity(company) {
    let score = 40; // Base score
    
    const employees = company.employees || 0;
    const turnover = company.turnover || 0;
    
    // Size-based scoring (larger companies = higher software spend)
    if (employees >= 500) score += 30;
    else if (employees >= 250) score += 25;
    else if (employees >= 100) score += 20;
    else if (employees >= 50) score += 15;
    
    // Revenue-based scoring
    if (turnover >= 50000000) score += 20; // €50M+
    else if (turnover >= 20000000) score += 15; // €20M+
    else if (turnover >= 10000000) score += 10; // €10M+
    
    return Math.min(score, 100);
  }

  /**
   * Score geographic priority for EU expansion
   */
  scoreEUGeographicPriority(company) {
    const country = company.country.toLowerCase();
    
    // Priority scoring based on market size and ESG maturity
    const priorityScores = {
      'germany': 95,      // Largest EU economy, strong ESG focus
      'france': 90,       // Large market, regulatory leadership
      'netherlands': 85,  // High ESG awareness, business-friendly
      'ireland': 80,      // EU headquarters hub, tech sector
      'united kingdom': 75, // Brexit but still important market
      'belgium': 70,      // EU institutions, multinational HQs
      'austria': 70,      // Strong environmental regulations
      'denmark': 70,      // ESG leadership, sustainability focus
      'sweden': 70,       // Nordic ESG standards
      'italy': 65,        // Large economy, growing ESG focus
      'spain': 65,        // Growing market
      'poland': 60,       // Large market, developing ESG
      'default': 50       // Other EU countries
    };
    
    // Check each priority level
    for (const [countryKey, score] of Object.entries(priorityScores)) {
      if (country.includes(countryKey) || country.includes(countryKey.replace(' ', ''))) {
        return score;
      }
    }
    
    return priorityScores.default;
  }

  /**
   * Generate EU-specific market insights
   */
  generateEUMarketInsights(company) {
    const insights = [];
    const country = company.country;
    const employees = company.employees || 0;
    
    // Country-specific insights
    if (country.toLowerCase().includes('germany')) {
      insights.push('German companies often lead EU ESG adoption - high conversion potential');
      if (employees >= 100) {
        insights.push('German mid-market companies typically have dedicated sustainability roles');
      }
    }
    
    if (country.toLowerCase().includes('france')) {
      insights.push('French companies face strong regulatory ESG pressure - urgent implementation needs');
    }
    
    if (country.toLowerCase().includes('netherlands')) {
      insights.push('Dutch companies are early ESG adopters - focus on advanced features');
    }

    // Size-based insights
    if (employees >= 500) {
      insights.push('Large company likely has ESG budget and dedicated team');
    } else if (employees >= 100) {
      insights.push('Mid-size company may need end-to-end ESG solution');
    } else if (employees >= 10) {
      insights.push('SME may benefit from simplified, cost-effective ESG tools');
    }

    return insights;
  }

  /**
   * Check if country is in EU for CSRD applicability
   */
  isEUCountry(country) {
    const euCountries = [
      'austria', 'belgium', 'bulgaria', 'croatia', 'cyprus', 'czech republic',
      'denmark', 'estonia', 'finland', 'france', 'germany', 'greece',
      'hungary', 'ireland', 'italy', 'latvia', 'lithuania', 'luxembourg',
      'malta', 'netherlands', 'poland', 'portugal', 'romania', 'slovakia',
      'slovenia', 'spain', 'sweden'
    ];
    
    const countryLower = country.toLowerCase();
    return euCountries.some(euCountry => countryLower.includes(euCountry));
  }

  /**
   * Check if country is high priority for EU expansion
   */
  isHighPriorityEUMarket(country) {
    const highPriorityCountries = ['germany', 'france', 'netherlands', 'ireland'];
    const countryLower = country.toLowerCase();
    
    return highPriorityCountries.some(priority => countryLower.includes(priority));
  }

  /**
   * Assess EU industry ESG risk with sector-specific considerations
   */
  assessEUIndustryRisk(industryCode, country) {
    // Base industry risk assessment
    let riskLevel = 'LOW';
    let category = 'Other';
    let specificRisks = [];

    const industryLower = (industryCode || '').toLowerCase();
    
    // High-risk industries in EU context
    const highRiskPatterns = [
      'manufacturing', 'energy', 'oil', 'gas', 'mining', 'chemical',
      'automotive', 'steel', 'cement', 'aviation', 'shipping'
    ];
    
    const mediumRiskPatterns = [
      'construction', 'transport', 'logistics', 'textile', 'food',
      'agriculture', 'retail', 'banking', 'insurance'
    ];
    
    if (highRiskPatterns.some(pattern => industryLower.includes(pattern))) {
      riskLevel = 'HIGH';
      category = 'High Environmental Impact';
      specificRisks = ['Carbon emissions', 'Environmental compliance', 'Supply chain sustainability'];
    } else if (mediumRiskPatterns.some(pattern => industryLower.includes(pattern))) {
      riskLevel = 'MEDIUM';
      category = 'Moderate Environmental Impact';
      specificRisks = ['Supply chain management', 'Social responsibility', 'Operational efficiency'];
    } else {
      riskLevel = 'LOW';
      category = 'Service/Technology';
      specificRisks = ['Digital sustainability', 'Employee welfare', 'Data governance'];
    }

    // Country-specific risk adjustments
    const countryLower = country.toLowerCase();
    if (countryLower.includes('germany') && riskLevel === 'HIGH') {
      specificRisks.push('German Supply Chain Act compliance');
    }
    
    if (countryLower.includes('france') && riskLevel !== 'LOW') {
      specificRisks.push('French Corporate Vigilance Law requirements');
    }

    return {
      level: riskLevel,
      category,
      specificRisks,
      regulatoryPressure: this.getCountryRegulatoryPressure(country)
    };
  }

  /**
   * Get regulatory pressure level by country
   */
  getCountryRegulatoryPressure(country) {
    const countryLower = country.toLowerCase();
    
    if (countryLower.includes('germany') || countryLower.includes('france')) {
      return 'VERY_HIGH'; // Strong national ESG regulations
    }
    if (countryLower.includes('netherlands') || countryLower.includes('denmark')) {
      return 'HIGH'; // Progressive ESG policies
    }
    if (countryLower.includes('ireland') || countryLower.includes('belgium')) {
      return 'MEDIUM'; // EU standards plus business-friendly
    }
    
    return 'MEDIUM'; // Standard EU regulatory environment
  }

  /**
   * Generate EU-specific ESG recommendations
   */
  generateEURecommendations(assessment, company) {
    const recommendations = [];
    const country = company.country;
    const requirement = assessment.primaryRequirement;
    
    // CSRD Mandatory recommendations
    if (requirement === 'CSRD_MANDATORY') {
      recommendations.push({
        category: 'URGENT_EU_COMPLIANCE',
        title: 'CSRD Implementation Required',
        description: `Company must implement CSRD reporting by ${assessment.csrd.deadline}`,
        actionItems: [
          'Conduct EU taxonomy alignment assessment',
          'Implement double materiality analysis',
          'Set up ESRS data collection processes',
          'Engage external ESG assurance provider',
          'Train finance/sustainability teams on CSRD'
        ],
        timeline: 'Immediate (0-6 months)',
        businessImpact: 'Critical for EU regulatory compliance',
        estimatedBudget: '€50,000 - €200,000',
        countrySpecific: this.getCountrySpecificCSRDRequirements(country)
      });
    }

    // Listed SME recommendations  
    if (requirement === 'CSRD_LISTED_SME') {
      recommendations.push({
        category: 'STRATEGIC_PREPARATION',
        title: 'Listed SME CSRD Preparation',
        description: 'Prepare for 2026 CSRD requirements with phased approach',
        actionItems: [
          'Begin materiality assessment process',
          'Pilot ESG data collection systems',
          'Engage with simplified ESRS standards',
          'Build internal ESG capabilities'
        ],
        timeline: 'Strategic (12-18 months)',
        businessImpact: 'Regulatory compliance + competitive positioning',
        estimatedBudget: '€25,000 - €75,000'
      });
    }

    // VSME eligible recommendations
    if (requirement === 'VSME_VOLUNTARY') {
      recommendations.push({
        category: 'COMPETITIVE_ADVANTAGE',
        title: 'VSME Framework Adoption for SMEs',
        description: 'Leverage voluntary simplified ESG standards for market differentiation',
        actionItems: [
          'Assess VSME framework applicability',
          'Implement simplified ESG metrics',
          'Develop sustainability communication strategy',
          'Engage supply chain on ESG requirements'
        ],
        timeline: 'Strategic (6-12 months)',
        businessImpact: 'Market differentiation and stakeholder confidence',
        estimatedBudget: '€10,000 - €30,000'
      });
    }

    // Country-specific recommendations
    recommendations.push(...this.getCountrySpecificRecommendations(country, company));

    // Industry-specific EU recommendations
    if (assessment.industryRisk.level === 'HIGH') {
      recommendations.push({
        category: 'INDUSTRY_LEADERSHIP',
        title: 'High-Risk Industry ESG Leadership',
        description: `${assessment.industryRisk.category} sector under intense EU regulatory scrutiny`,
        actionItems: [
          'Implement sector-specific ESG frameworks',
          'Join industry sustainability initiatives',
          'Conduct lifecycle assessments',
          'Develop science-based targets',
          'Engage with EU taxonomy requirements'
        ],
        timeline: 'Strategic (12-24 months)',
        businessImpact: 'Industry leadership and risk mitigation',
        estimatedBudget: '€75,000 - €300,000'
      });
    }

    return recommendations;
  }

  /**
   * Get country-specific CSRD requirements
   */
  getCountrySpecificCSRDRequirements(country) {
    const countryLower = country.toLowerCase();
    
    if (countryLower.includes('germany')) {
      return [
        'German Supply Chain Act (LkSG) integration required',
        'BaFin supervisory expectations for financial sector',
        'German Sustainability Code alignment opportunities'
      ];
    }
    
    if (countryLower.includes('france')) {
      return [
        'French Corporate Vigilance Law overlap considerations',
        'AMF guidance for listed companies',
        'Integration with existing French ESG reporting'
      ];
    }
    
    if (countryLower.includes('netherlands')) {
      return [
        'Dutch Banking Code ESG requirements',
        'SER advisory council ESG guidance',
        'Integration with Dutch pension fund ESG requirements'
      ];
    }

    return ['Standard CSRD implementation across EU member state'];
  }

  /**
   * Get country-specific recommendations
   */
  getCountrySpecificRecommendations(country, company) {
    const recommendations = [];
    const countryLower = country.toLowerCase();
    
    if (countryLower.includes('germany')) {
      recommendations.push({
        category: 'GERMAN_MARKET',
        title: 'German ESG Market Strategy',
        description: 'Leverage Germany\'s ESG leadership position',
        actionItems: [
          'Align with German Sustainability Code',
          'Consider B-Corp certification popular in Germany',
          'Engage with German industry associations',
          'Leverage strong German sustainability consulting ecosystem'
        ],
        timeline: 'Market entry (3-6 months)',
        businessImpact: 'Enhanced credibility in largest EU market'
      });
    }

    if (countryLower.includes('france')) {
      recommendations.push({
        category: 'FRENCH_MARKET',
        title: 'French ESG Regulatory Landscape',
        description: 'Navigate France\'s comprehensive ESG framework',
        actionItems: [
          'Understand Article 173 Energy Transition Law',
          'Align with French Vigilance Law if applicable',
          'Consider French B-Corp movement participation',
          'Leverage strong French impact investing ecosystem'
        ],
        timeline: 'Regulatory alignment (6-12 months)',
        businessImpact: 'Compliance with French ESG ecosystem'
      });
    }

    return recommendations;
  }

  /**
   * Enhanced deduplication for EU companies
   */
  deduplicateCompanies(companies) {
    const seen = new Map();
    const unique = [];

    for (const company of companies) {
      // Enhanced deduplication key considering VAT numbers and registry numbers
      const key = this.generateAdvancedCompanyKey(company);
      
      if (!seen.has(key)) {
        seen.set(key, company);
        unique.push(company);
      } else {
        // Merge data from multiple sources for the same company
        const existing = seen.get(key);
        const merged = this.mergeCompanyData(existing, company);
        seen.set(key, merged);
        
        const index = unique.findIndex(c => c.id === existing.id);
        if (index >= 0) {
          unique[index] = merged;
        }
      }
    }

    return unique;
  }

  /**
   * Generate advanced company key for deduplication
   */
  generateAdvancedCompanyKey(company) {
    // Normalize company name
    const normalizedName = company.name
      .toLowerCase()
      .replace(/\b(ltd|limited|gmbh|sarl|bv|ab|spa|srl|s\.r\.l|s\.p\.a|ag|inc|corp|corporation)\b/g, '')
      .replace(/[^\w\s]/g, '')
      .trim();
    
    const country = company.country.toLowerCase().replace(/\s+/g, '');
    
    // Use VAT number if available as primary identifier
    if (company.vatNumber) {
      return `vat_${company.vatNumber.replace(/\s/g, '')}`;
    }
    
    // Use registry number if available
    if (company.registryNumber || company.companyNumber) {
      const regNumber = company.registryNumber || company.companyNumber;
      return `reg_${country}_${regNumber}`;
    }
    
    // Fallback to name + country
    return `name_${normalizedName}_${country}`;
  }

  /**
   * Merge data from multiple sources for the same company
   */
  mergeCompanyData(existing, newData) {
    const merged = { ...existing };
    
    // Prefer more complete data fields
    Object.keys(newData).forEach(key => {
      if (newData[key] && (!existing[key] || newData[key].toString().length > existing[key].toString().length)) {
        merged[key] = newData[key];
      }
    });
    
    // Combine sources
    const existingSources = Array.isArray(existing.sources) ? existing.sources : [existing.source].filter(Boolean);
    const newSources = Array.isArray(newData.sources) ? newData.sources : [newData.source].filter(Boolean);
    merged.sources = [...new Set([...existingSources, ...newSources])];
    
    // Merge ESG metadata
    if (existing.esgMetadata && newData.esgMetadata) {
      merged.esgMetadata = {
        ...existing.esgMetadata,
        ...newData.esgMetadata
      };
    }
    
    merged.lastUpdated = new Date().toISOString();
    merged.dataMerged = true;
    
    return merged;
  }

  /**
   * Estimate UK company employees from SIC codes and other indicators
   */
  estimateUKEmployees(company) {
    // Use any existing employee data
    if (company.employees) return company.employees;
    
    // Estimate based on company type and available data
    const companyType = company.company_type || '';
    
    if (companyType.includes('plc') || companyType.includes('public')) {
      return Math.floor(Math.random() * 2000) + 500; // Large public company estimate
    }
    
    if (companyType.includes('private-limited')) {
      return Math.floor(Math.random() * 200) + 10; // SME estimate
    }
    
    return Math.floor(Math.random() * 100) + 5; // Default SME estimate
  }

  /**
   * Estimate UK company turnover
   */
  estimateUKTurnover(company) {
    if (company.turnover) return company.turnover;
    
    const employees = this.estimateUKEmployees(company);
    
    // Very rough estimation: average revenue per employee varies by sector
    const revenuePerEmployee = 150000; // £150k average
    return employees * revenuePerEmployee;
  }

  /**
   * Assess UK industry ESG risk from SIC codes
   */
  assessUKIndustryRisk(sicCodes) {
    if (!sicCodes || sicCodes.length === 0) return 'LOW';
    
    const highRiskSicPrefixes = ['01', '05', '06', '07', '08', '09', '10', '20', '35'];
    const mediumRiskSicPrefixes = ['25', '41', '49', '50', '51'];
    
    for (const sicCode of sicCodes) {
      const prefix = sicCode.substring(0, 2);
      if (highRiskSicPrefixes.includes(prefix)) {
        return 'HIGH';
      }
      if (mediumRiskSicPrefixes.includes(prefix)) {
        return 'MEDIUM';
      }
    }
    
    return 'LOW';
  }

  /**
   * Get UK CSRD deadline estimate
   */
  getUKCSRDDeadline(company) {
    const employees = this.estimateUKEmployees(company);
    
    // Post-Brexit, UK companies may still need CSRD if they operate in EU
    if (employees >= 500) return '2025_IF_EU_SUBSIDIARY';
    return 'UK_SEPARATE_FRAMEWORK';
  }

  /**
   * Enhanced VAT validation and enrichment for EU companies
   * @param {Array} companies - Companies to enrich with VAT data
   * @returns {Promise<Array>} Enriched companies with VAT validation
   */
  async enrichWithVATValidation(companies) {
    const enrichedCompanies = [];
    
    for (const company of companies) {
      try {
        let enrichedCompany = { ...company };
        
        // Extract VAT number if available
        if (company.vatNumber) {
          const countryCode = company.vatNumber.substring(0, 2);
          const vatNumber = company.vatNumber.substring(2);
          
          console.log(`Validating VAT for ${company.name}: ${company.vatNumber}`);
          
          const viesValidation = await this.dataSources.vies.validateVATNumber(vatNumber, countryCode);
          
          if (viesValidation) {
            enrichedCompany.viesValidation = {
              valid: true,
              validatedName: viesValidation.name,
              validatedAddress: viesValidation.address,
              validationDate: viesValidation.lastUpdated
            };
            
            console.log(`VAT validated for ${company.name}`);
          }
        }
        
        enrichedCompanies.push(enrichedCompany);
        
        // Rate limiting for VIES API
        await new Promise(resolve => setTimeout(resolve, 1000));
        
      } catch (error) {
        console.warn(`VAT validation failed for ${company.name}:`, error);
        enrichedCompanies.push(company); // Include without VAT data
      }
    }
    
    return enrichedCompanies;
  }

  /**
   * Get detailed ESG analysis for a specific EU company
   * @param {string} companyId - Company identifier (with country prefix)
   * @returns {Promise<Object>} Detailed EU ESG analysis
   */
  async getDetailedEUESGAnalysis(companyId) {
    try {
      console.log(`Getting detailed EU ESG analysis for: ${companyId}`);
      
      // Parse company ID to determine source and registry
      const [country, ...idParts] = companyId.split('_');
      const actualId = idParts.join('_');
      
      let detailedCompany = null;
      
      // Get detailed company data based on source
      switch (country) {
        case 'uk':
          detailedCompany = await this.dataSources.companiesHouse.getCompanyDetails(actualId);
          break;
          
        case 'de':
          detailedCompany = await this.dataSources.euRegistry.registries.german.getCompanyDetails(actualId);
          break;
          
        case 'fr':
          detailedCompany = await this.dataSources.euRegistry.registries.french.getCompanyDetails(actualId);
          break;
          
        case 'nl':
          detailedCompany = await this.dataSources.euRegistry.registries.dutch.getCompanyDetails(actualId);
          break;
          
        default:
          throw new Error(`Unsupported country code: ${country}`);
      }
      
      if (!detailedCompany) {
        throw new Error('Company not found in registry');
      }
      
      // Run comprehensive EU ESG assessment
      const esgAssessment = await this.performEUESGAssessment(detailedCompany);
      
      // Calculate detailed EU scoring
      const scoringResult = await this.calculateEUESGScore({
        ...detailedCompany,
        esgAssessment
      });

      // Generate EU market insights
      const marketInsights = this.generateEUMarketInsights(detailedCompany);
      
      // Get competitive landscape analysis
      const competitiveAnalysis = await this.analyzeEUCompetitiveLandscape(detailedCompany);

      return {
        company: detailedCompany,
        esgAssessment,
        scoring: scoringResult,
        marketInsights,
        competitiveAnalysis,
        recommendations: this.generateEURecommendations(esgAssessment, detailedCompany),
        lastAnalyzed: new Date().toISOString()
      };

    } catch (error) {
      console.error(`Failed to get detailed EU ESG analysis for ${companyId}:`, error);
      throw new Error(`EU analysis failed: ${error.message}`);
    }
  }

  /**
   * Analyze competitive landscape in EU market
   */
  async analyzeEUCompetitiveLandscape(company) {
    try {
      const industry = company.industry;
      const country = company.country;
      const employees = company.employees || 0;
      
      // Find similar companies for benchmarking
      const similarCompaniesQuery = `${industry} ${country}`.trim();
      const benchmarkCompanies = await this.aggregateEUCompanyData(
        similarCompaniesQuery, 
        20, 
        [country.toLowerCase()]
      );
      
      // Filter to similar size companies
      const sizeRange = this.getCompanySizeRange(employees);
      const similarSized = benchmarkCompanies.filter(comp => {
        const compSize = this.getCompanySizeRange(comp.employees || 0);
        return compSize === sizeRange;
      });

      return {
        industryPeers: similarSized.length,
        averageSize: this.calculateAverageSize(similarSized),
        esgMaturityLevel: this.estimateIndustryESGMaturity(industry, country),
        competitiveAdvantageOpportunity: this.assessCompetitiveAdvantage(company, similarSized),
        marketPosition: this.assessMarketPosition(company, similarSized)
      };
      
    } catch (error) {
      console.warn('Competitive analysis failed:', error);
      return {
        industryPeers: 'Unknown',
        esgMaturityLevel: 'Developing',
        competitiveAdvantageOpportunity: 'Medium'
      };
    }
  }

  /**
   * Get company size range category
   */
  getCompanySizeRange(employees) {
    if (employees >= 500) return 'LARGE';
    if (employees >= 250) return 'MEDIUM_LARGE';
    if (employees >= 50) return 'MEDIUM';
    if (employees >= 10) return 'SMALL';
    return 'MICRO';
  }

  /**
   * Calculate average size metrics for benchmark companies
   */
  calculateAverageSize(companies) {
    if (companies.length === 0) return { employees: 0, turnover: 0 };
    
    const totalEmployees = companies.reduce((sum, comp) => sum + (comp.employees || 0), 0);
    const totalTurnover = companies.reduce((sum, comp) => sum + (comp.turnover || 0), 0);
    
    return {
      employees: Math.round(totalEmployees / companies.length),
      turnover: Math.round(totalTurnover / companies.length)
    };
  }

  /**
   * Estimate industry ESG maturity level
   */
  estimateIndustryESGMaturity(industry, country) {
    const industryLower = (industry || '').toLowerCase();
    const countryLower = country.toLowerCase();
    
    // High maturity industries
    if (['financial', 'banking', 'insurance', 'energy', 'utility'].some(term => industryLower.includes(term))) {
      return countryLower.includes('germany') || countryLower.includes('netherlands') ? 'ADVANCED' : 'DEVELOPING';
    }
    
    // Medium maturity industries
    if (['manufacturing', 'automotive', 'chemical'].some(term => industryLower.includes(term))) {
      return 'DEVELOPING';
    }
    
    // Emerging ESG focus
    return 'EMERGING';
  }

  /**
   * Assess competitive advantage opportunity
   */
  assessCompetitiveAdvantage(company, peers) {
    if (peers.length === 0) return 'HIGH'; // First mover advantage
    
    const peerESGCount = peers.filter(peer => 
      peer.esgIndicators?.csrdApplicable || peer.esgIndicators?.vsmeEligible
    ).length;
    
    const esgAdoptionRate = peerESGCount / peers.length;
    
    if (esgAdoptionRate < 0.3) return 'HIGH'; // Low adoption = high opportunity
    if (esgAdoptionRate < 0.6) return 'MEDIUM';
    return 'LOW'; // Market already saturated
  }

  /**
   * Assess market position relative to peers
   */
  assessMarketPosition(company, peers) {
    if (peers.length === 0) return 'STANDALONE';
    
    const companyEmployees = company.employees || 0;
    const largerPeers = peers.filter(peer => (peer.employees || 0) > companyEmployees).length;
    
    const percentile = ((peers.length - largerPeers) / peers.length) * 100;
    
    if (percentile >= 80) return 'MARKET_LEADER';
    if (percentile >= 60) return 'LARGE_PLAYER';
    if (percentile >= 40) return 'MIDDLE_MARKET';
    if (percentile >= 20) return 'SMALLER_PLAYER';
    return 'NICHE_PLAYER';
  }

  /**
   * Rank leads with EU-specific priority weighting
   */
  rankLeads(leads) {
    return leads.sort((a, b) => {
      // EU-specific priority ranking
      const priorityOrder = { 
        'CRITICAL': 5, 'HIGH': 4, 'MEDIUM': 3, 'LOW': 2, 'MINIMAL': 1 
      };
      
      const priorityDiff = (priorityOrder[b.priority] || 0) - (priorityOrder[a.priority] || 0);
      if (priorityDiff !== 0) return priorityDiff;
      
      // Then by total score
      const scoreDiff = (b.totalScore || 0) - (a.totalScore || 0);
      if (scoreDiff !== 0) return scoreDiff;
      
      // Finally by EU geographic priority
      const aGeoPriority = this.scoreEUGeographicPriority(a);
      const bGeoPriority = this.scoreEUGeographicPriority(b);
      return bGeoPriority - aGeoPriority;
    });
  }

  /**
   * Export EU companies for CRM integration
   * @param {Array} companies - Selected companies to export
   * @param {string} format - Export format ('csv', 'hubspot', 'salesforce')
   * @returns {Promise<Object>} Export result
   */
  async exportEUCompanies(companies, format = 'csv') {
    try {
      const exportData = companies.map(company => ({
        // Standard CRM fields
        companyName: company.name,
        country: company.country,
        city: company.city,
        address: company.address,
        website: company.website,
        industry: company.industry,
        employees: company.employees,
        estimatedRevenue: company.turnover,
        
        // ESG-specific fields
        esgComplianceType: company.esgAssessment?.primaryRequirement,
        esgPriority: company.priority,
        esgScore: company.totalScore,
        csrdDeadline: company.esgAssessment?.csrd?.deadline,
        industryESGRisk: company.esgAssessment?.industryRisk?.level,
        
        // EU-specific fields
        vatNumber: company.vatNumber,
        registryNumber: company.registryNumber,
        legalForm: company.legalForm,
        registrySource: company.source,
        
        // Lead qualification
        leadScore: company.totalScore,
        leadReasoning: company.reasoning?.join('; '),
        lastUpdated: company.lastUpdated,
        
        // Sales enablement
        keyESGChallenges: company.esgAssessment?.industryRisk?.specificRisks?.join(', '),
        estimatedESGBudget: this.estimateESGBudget(company),
        competitivePosition: company.competitiveAnalysis?.marketPosition,
        salesApproachRecommendation: this.generateSalesApproach(company)
      }));

      const result = {
        exportedCount: exportData.length,
        format: format,
        timestamp: new Date().toISOString(),
        data: exportData
      };

      // Format-specific processing
      switch (format.toLowerCase()) {
        case 'csv':
          result.csvData = this.convertToCSV(exportData);
          break;
          
        case 'hubspot':
          result.hubspotPayload = this.formatForHubSpot(exportData);
          break;
          
        case 'salesforce':
          result.salesforcePayload = this.formatForSalesforce(exportData);
          break;
      }

      return result;
      
    } catch (error) {
      throw new Error(`EU export failed: ${error.message}`);
    }
  }

  /**
   * Estimate ESG software budget based on company characteristics
   */
  estimateESGBudget(company) {
    const employees = company.employees || 0;
    const requirement = company.esgAssessment?.primaryRequirement;
    
    let baseBudget = 10000; // €10k baseline
    
    // Size multiplier
    if (employees >= 500) baseBudget *= 5;       // €50k+
    else if (employees >= 250) baseBudget *= 3;  // €30k+
    else if (employees >= 100) baseBudget *= 2;  // €20k+
    
    // Compliance urgency multiplier
    switch (requirement) {
      case 'CSRD_MANDATORY':
        baseBudget *= 2; // Urgent need = higher budget
        break;
      case 'CSRD_LISTED_SME':
        baseBudget *= 1.5;
        break;
      case 'VSME_VOLUNTARY':
        baseBudget *= 1.2;
        break;
    }
    
    return `€${baseBudget.toLocaleString()} - €${(baseBudget * 2).toLocaleString()}`;
  }

  /**
   * Generate sales approach recommendation
   */
  generateSalesApproach(company) {
    const requirement = company.esgAssessment?.primaryRequirement;
    const riskLevel = company.esgAssessment?.industryRisk?.level;
    const country = company.country;
    
    let approach = '';
    
    // Compliance-driven approach
    if (requirement === 'CSRD_MANDATORY') {
      approach = 'URGENCY: Focus on compliance deadline and regulatory risk. ';
    } else if (requirement === 'VSME_VOLUNTARY') {
      approach = 'VALUE: Emphasize competitive advantage and stakeholder confidence. ';
    }
    
    // Industry-specific approach
    if (riskLevel === 'HIGH') {
      approach += 'Highlight industry-specific ESG risks and reputation management. ';
    }
    
    // Country-specific approach
    if (country.toLowerCase().includes('germany')) {
      approach += 'Reference German Supply Chain Act and sustainability leadership expectations.';
    } else if (country.toLowerCase().includes('france')) {
      approach += 'Discuss alignment with French Corporate Vigilance Law and sustainability goals.';
    } else if (country.toLowerCase().includes('netherlands')) {
      approach += 'Focus on advanced ESG capabilities and Dutch sustainability innovation.';
    }
    
    return approach.trim() || 'Standard ESG value proposition';
  }

  /**
   * Convert data to CSV format
   */
  convertToCSV(data) {
    if (data.length === 0) return '';
    
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(row => 
      Object.values(row).map(value => 
        typeof value === 'string' && value.includes(',') ? `"${value}"` : value
      ).join(',')
    );
    
    return [headers, ...rows].join('\n');
  }

  /**
   * Format data for HubSpot CRM
   */
  formatForHubSpot(data) {
    return data.map(company => ({
      properties: {
        name: company.companyName,
        domain: company.website,
        country: company.country,
        city: company.city,
        industry: company.industry,
        numberofemployees: company.employees,
        annualrevenue: company.estimatedRevenue,
        // Custom ESG properties (must be created in HubSpot)
        esg_compliance_type: company.esgComplianceType,
        esg_priority_level: company.esgPriority,
        esg_score: company.esgScore,
        csrd_deadline: company.csrdDeadline,
        lead_source: 'Digital Trent EU Registry Search'
      }
    }));
  }

  /**
   * Format data for Salesforce CRM
   */
  formatForSalesforce(data) {
    return data.map(company => ({
      Name: company.companyName,
      Website: company.website,
      BillingCountry: company.country,
      BillingCity: company.city,
      Industry: company.industry,
      NumberOfEmployees: company.employees,
      AnnualRevenue: company.estimatedRevenue,
      // Custom ESG fields (must be created in Salesforce)
      ESG_Compliance_Type__c: company.esgComplianceType,
      ESG_Priority_Level__c: company.esgPriority,
      ESG_Score__c: company.esgScore,
      CSRD_Deadline__c: company.csrdDeadline,
      Lead_Source: 'Digital Trent EU Registry Search'
    }));
  }

  /**
   * Get EU-wide ESG market statistics and insights
   * @param {Object} searchFilters - Current search filters
   * @returns {Promise<Object>} EU market insights
   */
  async getEUMarketInsights(searchFilters = {}) {
    try {
      const insights = {
        marketSize: {},
        complianceBreakdown: {},
        countryAnalysis: {},
        industryAnalysis: {},
        opportunityAssessment: {},
        timestamp: new Date().toISOString()
      };

      // Sample data for different countries to understand market size
      const sampleQueries = ['manufacturing', 'technology', 'consulting', 'retail'];
      const targetCountries = this.determineTargetCountries(searchFilters);
      
      for (const country of targetCountries) {
        const countryStats = {
          totalCompaniesFound: 0,
          csrdMandatory: 0,
          vsmeEligible: 0,
          highRiskIndustry: 0,
          averageEmployees: 0
        };

        // Quick sampling across industries
        for (const query of sampleQueries) {
          try {
            const sampleResults = await this.aggregateEUCompanyData(query, 10, [country]);
            const analyzed = await this.analyzeEUESGCompliance(sampleResults);
            
            countryStats.totalCompaniesFound += sampleResults.length;
            countryStats.csrdMandatory += analyzed.filter(c => 
              c.esgAssessment?.primaryRequirement === 'CSRD_MANDATORY'
            ).length;
            countryStats.vsmeEligible += analyzed.filter(c => 
              c.esgAssessment?.primaryRequirement === 'VSME_VOLUNTARY'
            ).length;
            countryStats.highRiskIndustry += analyzed.filter(c => 
              c.esgAssessment?.industryRisk?.level === 'HIGH'
            ).length;
            
            const employeeCounts = analyzed.map(c => c.employees || 0).filter(e => e > 0);
            if (employeeCounts.length > 0) {
              countryStats.averageEmployees = Math.round(
                employeeCounts.reduce((sum, e) => sum + e, 0) / employeeCounts.length
              );
            }
            
          } catch (error) {
            console.warn(`Sample failed for ${country} ${query}:`, error);
          }
        }
        
        insights.countryAnalysis[country] = countryStats;
      }

      // Calculate overall market insights
      insights.marketSize = {
        estimatedTotalCompanies: Object.values(insights.countryAnalysis)
          .reduce((sum, country) => sum + country.totalCompaniesFound, 0) * 250, // Extrapolate
        csrdMandatoryEstimate: Object.values(insights.countryAnalysis)
          .reduce((sum, country) => sum + country.csrdMandatory, 0) * 500,
        vsmeEligibleEstimate: Object.values(insights.countryAnalysis)
          .reduce((sum, country) => sum + country.vsmeEligible, 0) * 300
      };

      insights.opportunityAssessment = {
        highPriorityMarkets: this.identifyHighPriorityMarkets(insights.countryAnalysis),
        estimatedMarketValue: this.estimateEUMarketValue(insights.marketSize),
        recommendedFocus: this.recommendMarketFocus(insights.countryAnalysis)
      };

      return insights;
      
    } catch (error) {
      console.error('EU market insights failed:', error);
      return {
        error: 'Market insights temporarily unavailable',
        timestamp: new Date().toISOString()
      };
    }
  }

  /**
   * Identify high priority markets based on data
   */
  identifyHighPriorityMarkets(countryAnalysis) {
    return Object.entries(countryAnalysis)
      .filter(([country, stats]) => 
        stats.csrdMandatory > 2 || stats.vsmeEligible > 3 || stats.totalCompaniesFound > 15
      )
      .sort((a, b) => {
        const aScore = a[1].csrdMandatory * 3 + a[1].vsmeEligible * 2 + a[1].totalCompaniesFound;
        const bScore = b[1].csrdMandatory * 3 + b[1].vsmeEligible * 2 + b[1].totalCompaniesFound;
        return bScore - aScore;
      })
      .slice(0, 5)
      .map(([country]) => country);
  }

  /**
   * Estimate EU ESG software market value
   */
  estimateEUMarketValue(marketSize) {
    const csrdValue = marketSize.csrdMandatoryEstimate * 75000; // €75k average per CSRD company
    const vsmeValue = marketSize.vsmeEligibleEstimate * 25000; // €25k average per VSME company
    
    return {
      csrdMarket: `€${(csrdValue / 1000000).toFixed(1)}M`,
      vsmeMarket: `€${(vsmeValue / 1000000).toFixed(1)}M`,
      totalEstimate: `€${((csrdValue + vsmeValue) / 1000000).toFixed(1)}M`,
      methodology: 'Based on average ESG software spend per company size category'
    };
  }

  /**
   * Recommend market focus strategy
   */
  recommendMarketFocus(countryAnalysis) {
    const recommendations = [];
    
    // Find countries with high CSRD mandatory counts
    const csrdLeaders = Object.entries(countryAnalysis)
      .filter(([country, stats]) => stats.csrdMandatory > 2)
      .sort((a, b) => b[1].csrdMandatory - a[1].csrdMandatory);
    
    if (csrdLeaders.length > 0) {
      recommendations.push({
        strategy: 'CSRD_URGENCY',
        targetCountries: csrdLeaders.slice(0, 3).map(([country]) => country),
        reasoning: 'Focus on countries with most CSRD mandatory companies for immediate revenue',
        timeline: 'Q1-Q2 2025'
      });
    }
    
    // Find countries with high VSME potential
    const vsmeLeaders = Object.entries(countryAnalysis)
      .filter(([country, stats]) => stats.vsmeEligible > 3)
      .sort((a, b) => b[1].vsmeEligible - a[1].vsmeEligible);
    
    if (vsmeLeaders.length > 0) {
      recommendations.push({
        strategy: 'VSME_GROWTH',
        targetCountries: vsmeLeaders.slice(0, 3).map(([country]) => country),
        reasoning: 'Build market share in VSME-eligible SME segment',
        timeline: 'Q3-Q4 2025'
      });
    }

    return recommendations;
  }

  /**
   * Bulk enrich EU companies with additional data sources
   * @param {Array} companies - Companies to enrich
   * @returns {Promise<Array>} Enriched companies
   */
  async bulkEnrichEUCompanies(companies) {
    try {
      console.log(`Starting bulk enrichment for ${companies.length} EU companies`);
      
      // Step 1: VAT validation enrichment
      const vatEnriched = await this.enrichWithVATValidation(companies);
      console.log('VAT validation completed');
      
      // Step 2: Industry classification enhancement
      const industryEnriched = await this.enhanceIndustryClassification(vatEnriched);
      console.log('Industry classification enhanced');
      
      // Step 3: Financial metrics estimation
      const financialEnriched = await this.enhanceFinancialMetrics(industryEnriched);
      console.log('Financial metrics enhanced');
      
      // Step 4: ESG readiness assessment
      const esgEnriched = await this.assessESGReadiness(financialEnriched);
      console.log('ESG readiness assessed');
      
      return esgEnriched;
      
    } catch (error) {
      console.error('Bulk enrichment failed:', error);
      return companies; // Return original data if enrichment fails
    }
  }

  /**
   * Enhance industry classification with EU NACE codes
   */
  async enhanceIndustryClassification(companies) {
    return companies.map(company => {
      const industry = company.industry || '';
      const industryCode = company.industryCode || '';
      
      // Map to standardized EU NACE codes
      const naceMapping = this.mapToNACECode(industry, industryCode);
      
      return {
        ...company,
        enhancedIndustryData: {
          naceCode: naceMapping.code,
          naceDivision: naceMapping.division,
          naceSection: naceMapping.section,
          esgRiskCategory: naceMapping.esgRisk,
          regulatoryIntensity: naceMapping.regulatoryIntensity
        }
      };
    });
  }

  /**
   * Map industry to NACE codes for EU standardization
   */
  mapToNACECode(industry, existingCode) {
    const industryLower = industry.toLowerCase();
    
    // Common industry to NACE mappings for ESG purposes
    const naceMappings = {
      'manufacturing': { code: '10-33', division: 'Manufacturing', section: 'C', esgRisk: 'HIGH', regulatoryIntensity: 'HIGH' },
      'energy': { code: '35', division: 'Electricity, gas, steam', section: 'D', esgRisk: 'HIGH', regulatoryIntensity: 'VERY_HIGH' },
      'construction': { code: '41-43', division: 'Construction', section: 'F', esgRisk: 'MEDIUM', regulatoryIntensity: 'MEDIUM' },
      'transport': { code: '49-53', division: 'Transportation', section: 'H', esgRisk: 'MEDIUM', regulatoryIntensity: 'MEDIUM' },
      'technology': { code: '62-63', division: 'Information services', section: 'J', esgRisk: 'LOW', regulatoryIntensity: 'LOW' },
      'consulting': { code: '70', division: 'Management consultancy', section: 'M', esgRisk: 'LOW', regulatoryIntensity: 'LOW' },
      'financial': { code: '64-66', division: 'Financial services', section: 'K', esgRisk: 'MEDIUM', regulatoryIntensity: 'HIGH' }
    };
    
    // Find best match
    for (const [key, mapping] of Object.entries(naceMappings)) {
      if (industryLower.includes(key)) {
        return mapping;
      }
    }
    
    // Use existing code if available
    if (existingCode) {
      return {
        code: existingCode,
        division: 'Unknown',
        section: 'Unknown', 
        esgRisk: 'MEDIUM',
        regulatoryIntensity: 'MEDIUM'
      };
    }
    
    // Default fallback
    return {
      code: '82', 
      division: 'Other business support', 
      section: 'N', 
      esgRisk: 'LOW',
      regulatoryIntensity: 'LOW'
    };
  }

  /**
   * Enhance financial metrics estimation
   */
  async enhanceFinancialMetrics(companies) {
    return companies.map(company => {
      const employees = company.employees || 0;
      const existingTurnover = company.turnover;
      
      let enhancedMetrics = {
        estimatedTurnover: existingTurnover,
        estimatedProfitMargin: 0,
        estimatedESGBudgetCapacity: 0,
        financialStability: 'UNKNOWN'
      };
      
      // Estimate turnover if missing
      if (!existingTurnover && employees > 0) {
        const industry = company.enhancedIndustryData?.naceSection || 'N';
        enhancedMetrics.estimatedTurnover = this.estimateTurnoverByIndustry(employees, industry);
      }
      
      // Estimate profit margins by industry
      enhancedMetrics.estimatedProfitMargin = this.estimateProfitMargin(
        company.enhancedIndustryData?.naceDivision || 'Other'
      );
      
      // Estimate ESG budget capacity
      enhancedMetrics.estimatedESGBudgetCapacity = this.calculateESGBudgetCapacity(
        enhancedMetrics.estimatedTurnover || existingTurnover || 0,
        enhancedMetrics.estimatedProfitMargin
      );
      
      return {
        ...company,
        enhancedFinancials: enhancedMetrics
      };
    });
  }

  /**
   * Estimate turnover by industry and employees
   */
  estimateTurnoverByIndustry(employees, naceSection) {
    // Revenue per employee by NACE section (rough estimates)
    const revenuePerEmployee = {
      'A': 180000, // Agriculture
      'B': 800000, // Mining
      'C': 350000, // Manufacturing  
      'D': 2500000, // Energy
      'E': 450000, // Water/waste
      'F': 280000, // Construction
      'G': 320000, // Trade
      'H': 180000, // Transport
      'I': 250000, // Accommodation
      'J': 420000, // Information
      'K': 180000, // Financial
      'L': 150000, // Real estate
      'M': 220000, // Professional
      'N': 180000, // Administrative
      'default': 250000
    };
    
    const multiplier = revenuePerEmployee[naceSection] || revenuePerEmployee.default;
    return employees * multiplier;
  }

  /**
   * Estimate profit margins by industry
   */
  estimateProfitMargin(naceDivision) {
    const marginMap = {
      'Manufacturing': 0.08,
      'Technology': 0.15,
      'Financial services': 0.12,
      'Construction': 0.05,
      'Energy': 0.10,
      'Consulting': 0.18,
      'default': 0.10
    };
    
    for (const [division, margin] of Object.entries(marginMap)) {
      if (naceDivision.toLowerCase().includes(division.toLowerCase())) {
        return margin;
      }
    }
    
    return marginMap.default;
  }

  /**
   * Calculate ESG budget capacity based on financials
   */
  calculateESGBudgetCapacity(turnover, profitMargin) {
    const estimatedProfit = turnover * profitMargin;
    
    // ESG budget typically 0.1-0.5% of turnover for SMEs, 0.5-2% for large companies
    let budgetPercentage = 0.002; // 0.2% default
    
    if (turnover >= 100000000) budgetPercentage = 0.01; // 1% for large companies
    else if (turnover >= 50000000) budgetPercentage = 0.005; // 0.5% for medium-large
    else if (turnover >= 10000000) budgetPercentage = 0.003; // 0.3% for medium
    
    return Math.round(turnover * budgetPercentage);
  }

  /**
   * Assess ESG readiness level
   */
  async assessESGReadiness(companies) {
    return companies.map(company => {
      const readinessIndicators = {
        hasWebsite: !!company.website,
        industryESGFocus: company.esgAssessment?.industryRisk?.level === 'HIGH',
        complianceUrgency: company.esgAssessment?.primaryRequirement !== 'NOT_APPLICABLE',
        companySize: (company.employees || 0) >= 50,
        financialCapacity: company.enhancedFinancials?.estimatedESGBudgetCapacity > 15000
      };
      
      const readinessScore = Object.values(readinessIndicators)
        .reduce((sum, indicator) => sum + (indicator ? 1 : 0), 0);
      
      let readinessLevel = 'LOW';
      if (readinessScore >= 4) readinessLevel = 'HIGH';
      else if (readinessScore >= 3) readinessLevel = 'MEDIUM';
      
      return {
        ...company,
        esgReadiness: {
          level: readinessLevel,
          score: readinessScore,
          indicators: readinessIndicators,
          primaryBarriers: this.identifyESGBarriers(readinessIndicators, company),
          engagementStrategy: this.recommendEngagementStrategy(readinessLevel, company)
        }
      };
    });
  }

  /**
   * Identify main barriers to ESG adoption
   */
  identifyESGBarriers(indicators, company) {
    const barriers = [];
    
    if (!indicators.hasWebsite) {
      barriers.push('Limited digital presence may indicate lower tech adoption readiness');
    }
    
    if (!indicators.financialCapacity) {
      barriers.push('Limited ESG budget capacity - focus on cost-effective solutions');
    }
    
    if (!indicators.companySize) {
      barriers.push('Small team size - may need simplified, low-maintenance solution');
    }
    
    if (!indicators.complianceUrgency) {
      barriers.push('No immediate compliance pressure - need value-focused approach');
    }
    
    return barriers.length > 0 ? barriers : ['No significant barriers identified'];
  }

  /**
   * Recommend engagement strategy based on readiness
   */
  recommendEngagementStrategy(readinessLevel, company) {
    const strategies = {
      'HIGH': {
        approach: 'SOLUTION_FOCUSED',
        message: 'Present comprehensive ESG solution with implementation timeline',
        nextSteps: ['Demo advanced features', 'Provide implementation roadmap', 'Discuss integration options'],
        timeline: 'Fast track (2-4 weeks to close)'
      },
      'MEDIUM': {
        approach: 'EDUCATION_PLUS_VALUE',
        message: 'Combine ESG education with clear business value proposition',
        nextSteps: ['ESG readiness assessment', 'ROI calculation', 'Pilot program proposal'],
        timeline: 'Standard cycle (4-8 weeks to close)'
      },
      'LOW': {
        approach: 'EDUCATION_FIRST',
        message: 'Focus on ESG education and future-proofing business value',
        nextSteps: ['ESG awareness workshop', 'Industry benchmark report', 'Long-term partnership discussion'],
        timeline: 'Long cycle (8-16 weeks to close)'
      }
    };
    
    return strategies[readinessLevel] || strategies['MEDIUM'];
  }
}

// Export the enhanced service
export const esgLeadService = new ESGLeadGenerationService();